import { useRef } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import useTranslation from '@/utils/hooks/translation/useTranslation';
dayjs.extend(utc);
dayjs.extend(timezone);

import { REMOVE_TOAST } from '@/hooks/context/toast/actions';
import { useToastsContext } from '@/hooks/context/toast/useToastsContext';

import { put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { EDIT_SCHEDULER_MASTER_POST, SCHEDULER_POST } from '@/lib/routes/apiEndpoints';
import { calendarViews } from '@/lib/scheduler/calendar/calendar';

const useEventDrop = () => {
    const { t: tErrors } = useTranslation('errors');
    const toastId = useRef(null);
    const { toastError, dispatchToasts } = useToastsContext();

    async function eventsHandling({
        event,
        schedulerPosts,
        mutateSchedulerPosts,
        timezone,
        updateCrossPosts = undefined
    }) {
        if (toastId.current) {
            dispatchToasts({
                type: REMOVE_TOAST,
                payload: toastId.current
            });
        }

        const postId = event?._def?.publicId;
        const masterId = event?._def?.extendedProps?.master_id;

        if (!postId) {
            return;
        }

        const view = event._context.viewApi.type ?? calendarViews.week;

        async function editPost(post, oldPost, updateCrossPosts, callbackSuccess) {
            const CancelToken = new AbortController();

            try {
                if (updateCrossPosts) {
                    await put(
                        injectEndpointUrlParams(EDIT_SCHEDULER_MASTER_POST, {
                            MASTER_ID: post.master_id
                        }),
                        { scheduled_at: post.scheduled_at, scheduled_at_option: 'custom' },
                        {
                            signal: CancelToken.signal
                        }
                    );
                } else {
                    await put(
                        injectEndpointUrlParams(SCHEDULER_POST, {
                            SPID: post.social_profile_id,
                            postId: post.id
                        }),
                        { ...post, scheduled_at_option: 'custom' },
                        {
                            signal: CancelToken.signal
                        }
                    );
                }
                if (typeof callbackSuccess === 'function') {
                    callbackSuccess();
                }
            } catch (e) {
                dispatchToasts({
                    type: REMOVE_TOAST,
                    payload: toastId.current
                });
                if (e?.message?.error === 'migration-upgrade-area-error') {
                    toastError({
                        title: t(
                            'scheduler:scheduler_funnel.submit_errors.migration_upgrade_area_error_banner'
                        ),
                        icon: 'calendar'
                    });
                } else {
                    toastError({
                        title: tErrors('error_occured_submit_data'),
                        icon: 'calendar'
                    });
                }

                if (oldPost) {
                    mutateSchedulerPosts();
                } else {
                    mutateSchedulerPosts(schedulerPosts, true);
                }
            }
        }

        const oldDataPost = schedulerPosts.find((post) => parseInt(postId) === parseInt(post.id));
        // Get hour/min/sec of the event
        const hourEvent = oldDataPost.startRef.split(' ')[1];
        const datePost = dayjs(oldDataPost.scheduled_at).utc().tz(timezone);
        let scheduleAtDate = `${dayjs(event.startStr).format('YYYY-MM-DDTHH:')}${datePost.format(
            'mm:ss'
        )}`;
        let startDate = event.startStr;

        if (view === calendarViews.month && event.source === null) {
            scheduleAtDate = `${event.startStr}T${datePost.format('HH:mm:ss')}`;
            startDate = `${event.startStr}T${hourEvent}`;
        }

        const utc = dayjs(startDate).utc().tz(timezone).format('Z');
        const drop_date = startDate.split('T');
        const drop_date_formatted = drop_date[0] + ' ' + drop_date[1];

        const currentDate = dayjs(dayjs().format('YYYY-MM-DD HH:mm:ss')).add(5, 'minute').utc();
        const isScheduledBeforeCurrentDate = dayjs(scheduleAtDate + utc)
            .utc()
            .isBefore(currentDate);

        let newPostScheduledAt = dayjs(scheduleAtDate + utc)
            .utc()
            .format();
        if (isScheduledBeforeCurrentDate) {
            newPostScheduledAt = currentDate.format();
        }

        const getUpdatedPost = (post, newPostScheduledAt, drop_date_formatted) => ({
            ...post,
            scheduled_at: newPostScheduledAt,
            start: drop_date_formatted,
            startRef: drop_date_formatted,
            startEditable: true
        });

        mutateSchedulerPosts(
            schedulerPosts.map((post) => {
                if (updateCrossPosts && parseInt(masterId) === parseInt(post?.master_id)) {
                    return getUpdatedPost(post, newPostScheduledAt, drop_date_formatted);
                }

                if (!updateCrossPosts && parseInt(postId) === parseInt(post?.id)) {
                    return getUpdatedPost(post, newPostScheduledAt, drop_date_formatted);
                }

                return post;
            }),
            false
        );

        await editPost(
            {
                ...oldDataPost,
                scheduled_at: newPostScheduledAt,
                start: drop_date_formatted,
                startRef: drop_date_formatted,
                startEditable: true
            },
            oldDataPost,
            updateCrossPosts
        );
    }

    return { eventsHandling: eventsHandling };
};

export default useEventDrop;
